import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import designationsService from '../../services/designationsService';
import departmentsService from '../../services/departmentsService';
import NotificationToast from '../../components/ui/NotificationToast';

const Designations = () => {
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDesig, setEditingDesig] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', department_id: '', min_experience: '', max_experience: '', status: 'active' });

  useEffect(() => {
    loadDesignations();
    loadDepartments();
  }, []);

  const loadDesignations = async () => {
    try {
      setLoading(true);
      const result = await designationsService.getDesignations({ search: searchTerm });
      if (result?.success) setDesignations(result.data || []);
    } catch (error) {
      console.error('Error loading designations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    const result = await departmentsService.getDepartments();
    if (result?.success) setDepartments(result.data || []);
  };

  useEffect(() => {
    const timer = setTimeout(() => loadDesignations(), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, min_experience: formData.min_experience ? parseInt(formData.min_experience) : null, max_experience: formData.max_experience ? parseInt(formData.max_experience) : null };
      const result = editingDesig
        ? await designationsService.updateDesignation(editingDesig.id, data)
        : await designationsService.createDesignation(data);
      
      if (result?.success) {
        setNotification({ message: `Designation ${editingDesig ? 'updated' : 'created'} successfully`, type: 'success' });
        setShowModal(false);
        setEditingDesig(null);
        setFormData({ name: '', description: '', department_id: '', min_experience: '', max_experience: '', status: 'active' });
        loadDesignations();
      } else {
        setNotification({ message: result?.error || 'Operation failed', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleEdit = (desig) => {
    setEditingDesig(desig);
    setFormData({ name: desig.name, description: desig.description || '', department_id: desig.department_id || '', min_experience: desig.min_experience || '', max_experience: desig.max_experience || '', status: desig.status });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this designation?')) return;
    try {
      const result = await designationsService.deleteDesignation(id);
      if (result?.success) {
        setNotification({ message: 'Designation deleted successfully', type: 'success' });
        loadDesignations();
      } else {
        setNotification({ message: result?.error || 'Failed to delete', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  return (
    <div className="p-6">
      <NotificationToast message={notification?.message} type={notification?.type} onDismiss={() => setNotification(null)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Designations</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage job roles and positions</p>
        </div>
        <Button onClick={() => { setEditingDesig(null); setFormData({ name: '', description: '', department_id: '', min_experience: '', max_experience: '', status: 'active' }); setShowModal(true); }}>
          <Icon name="Plus" size={16} className="mr-2" />
          Add Designation
        </Button>
      </div>
      <div className="mb-4">
        <Input placeholder="Search designations..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full max-w-md" iconName="Search" />
      </div>
      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" /><p className="text-muted-foreground">Loading...</p></div>
      ) : designations.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="Award" size={48} className="mx-auto mb-4 opacity-50" />
          <p>No designations found</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Designation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Experience
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {designations.map((desig) => (
                  <tr key={desig.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Icon name="Award" size={20} className="text-purple-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {desig.name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      {desig.department_name || 'N/A'}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-foreground max-w-[200px] truncate">
                        {desig.description || 'N/A'}
                      </p>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      {(desig.min_experience || desig.max_experience) ? (
                        <span>{desig.min_experience || 0} - {desig.max_experience || '∞'} years</span>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        desig.status === 'active' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      }`}>
                        {desig.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(desig)}
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(desig.id)}
                          className="text-destructive"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowModal(false); setEditingDesig(null); }}>
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{editingDesig ? 'Edit' : 'Add'} Designation</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input label="Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" rows="3" placeholder="Description" />
              <select value={formData.department_id} onChange={(e) => setFormData({...formData, department_id: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md">
                <option value="">Select Department</option>
                {departments.map(dept => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Min Experience (years)" type="number" value={formData.min_experience} onChange={(e) => setFormData({...formData, min_experience: e.target.value})} />
                <Input label="Max Experience (years)" type="number" value={formData.max_experience} onChange={(e) => setFormData({...formData, max_experience: e.target.value})} />
              </div>
              <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingDesig(null); }}>Cancel</Button>
                <Button type="submit">Save</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Designations;

